import React, { useEffect, useMemo, useState } from 'react';
import HeroCarousel from '../components/HeroCarousel';
import TrustBadges from '../components/TrustBadges';
import CategoriesGrid from '../components/CategoriesGrid';
import ProductCarousel from '../components/ProductCarousel';
import ProductCard from '../components/ProductCard';
import { PRODUCTS, REVIEWS } from '../mock/mockData';
import { getCatalogProducts } from '../services/catalogService';
import { Star, Sparkles, Truck, Award } from 'lucide-react';
import { Link } from 'react-router-dom';

const HomePage = () => {
  const [catalog, setCatalog] = useState(PRODUCTS);

  useEffect(() => {
    let mounted = true;
    getCatalogProducts({ limit: 40 }).then(({ products }) => {
      if (mounted) setCatalog(products);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const bestSellers = useMemo(() => (catalog.length ? catalog : PRODUCTS).slice(0, 12), [catalog]);
  const newLaunches = useMemo(() => [...(catalog.length ? catalog : PRODUCTS)].reverse().slice(0, 12), [catalog]);
  const beds = useMemo(() => {
    const bedroomProducts = catalog.filter((p) => p.category === 'beds').concat(catalog.filter((p) => p.category === 'storage')).slice(0, 8);
    if (bedroomProducts.length) return bedroomProducts;
    return PRODUCTS.filter((p) => p.category === 'beds').concat(PRODUCTS.filter((p) => p.category === 'storage')).slice(0, 8);
  }, [catalog]);

  return (
    <main className="bg-[#FAF7F2]">
      <HeroCarousel />
      <TrustBadges />
      <CategoriesGrid />

      <ProductCarousel
        title="Best Selling Products"
        subtitle="Our best-selling sofa sets, beds and dining furniture — trusted for comfort, quality and everyday use."
        products={bestSellers}
      />

      {/* Brand Strip */}
      <section className="bg-[#1F2A44] text-[#FAF7F2] py-16 md:py-20 mt-8">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <span className="text-[#E8B530] text-xs uppercase tracking-[0.3em] font-semibold">India's Trusted Brand</span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-tight">Making Homes Beautiful, One Piece at a Time</h2>
            <p className="mt-5 text-[#FAF7F2]/80 leading-relaxed">Buy premium furniture online — sofa sets, recliners, wooden beds, fabric sofas, and dining sets. Crafted in India with care, delivered to your door.</p>
            <Link to="/shop" className="inline-block mt-7 bg-[#E8B530] hover:bg-[#D4A220] text-[#1F2A44] font-semibold px-7 py-3 rounded-sm uppercase tracking-wider text-sm transition-colors">Explore Collection</Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { Icon: Sparkles, title: '10,000+', sub: 'Happy Customers' },
              { Icon: Award, title: '4.8★', sub: 'Google Rating' },
              { Icon: Truck, title: '7 Days', sub: 'Fast Delivery' },
              { Icon: Star, title: '15+', sub: 'Showrooms' },
            ].map((s, i) => (
              <div key={i} className="border border-[#FAF7F2]/15 p-6 hover:border-[#E8B530] transition-colors">
                <s.Icon className="text-[#E8B530]" size={24} />
                <p className="font-serif text-3xl font-bold mt-2">{s.title}</p>
                <p className="text-[#FAF7F2]/70 text-sm">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ProductCarousel
        title="New Launches"
        subtitle="Explore our latest collection of sofas, beds, and dining sets, crafted to bring timeless elegance to your home."
        products={newLaunches}
      />

      {/* Bedroom Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1F2A44]">Bedroom Furniture</h2>
          <p className="mt-2 text-[#1F2A44]/70 max-w-2xl mx-auto text-sm md:text-base">
            Solid wood king, queen, upholstered and storage beds. Comfortable, sturdy, and termite-safe for a lifetime.
          </p>
          <span className="inline-block mt-4 h-[2px] w-16 bg-[#E8B530]"></span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {beds.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
        <div className="text-center mt-10">
          <Link to="/shop/beds" className="inline-block border border-[#1F2A44] hover:bg-[#1F2A44] hover:text-[#FAF7F2] text-[#1F2A44] px-7 py-3 text-sm font-semibold uppercase tracking-wider transition-colors">View All Beds</Link>
        </div>
      </section>

      {/* Reviews */}
      <section className="bg-[#F3EFE8] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1F2A44]">Loved by Thousands of Homes</h2>
            <span className="inline-block mt-4 h-[2px] w-16 bg-[#E8B530]"></span>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {REVIEWS.map((r, i) => (
              <div key={i} className="bg-[#FAF7F2] p-6 border border-[#1F2A44]/10 hover:border-[#E8B530] transition-colors hover:-translate-y-1 duration-300">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} size={14} className="fill-[#E8B530] text-[#E8B530]" />
                  ))}
                </div>
                <p className="text-[#1F2A44]/80 text-sm leading-relaxed italic">“{r.text}”</p>
                <div className="mt-4 pt-4 border-t border-[#1F2A44]/10">
                  <p className="font-semibold text-[#1F2A44] text-sm">{r.name}</p>
                  <p className="text-xs text-[#1F2A44]/60">{r.city}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Promo strip */}
      <section className="bg-[#E8B530] py-6">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-center">
          <p className="text-[#1F2A44] font-bold text-base md:text-lg">SUMMER SALE · SAVE UP TO 75% OFF</p>
          <Link to="/shop" className="text-[#1F2A44] font-semibold underline underline-offset-4 hover:no-underline text-sm">Shop Now</Link>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
